/**
 * How an audit entry's actor is named, when the change may have been made through impersonation.
 *
 * A redeemed impersonation handover carries the **target's** Cognito tokens, so the actor recorded on an entry
 * is the account the change was made *through* — the customer's own user — and not the member of our staff who
 * was actually at the keyboard. Rendering that name alone tells a customer they made changes we made for them.
 *
 * That reading is worst exactly where it matters most: "AI suggestions published" on a document template, or a
 * republished workflow, both of which change what generates their court documents. So the entry carries
 * `impersonated_by` and this is the one place that turns the pair into a sentence.
 *
 * Shared rather than repeated because three surfaces show it — the Document Templates history tab and the two
 * workflow builder hosts — and three copies of a phrasing is three places for it to drift.
 */
const SUPPORT_SUFFIX = '(EasyEviction support, in your account)';

export function describeAuditActor(
  recordedName: string | null | undefined,
  impersonatedBy?: string | null
): string {
  const onBehalfOf = impersonatedBy?.trim();
  if (onBehalfOf) {
    return `${onBehalfOf} ${SUPPORT_SUFFIX}`;
  }
  return recordedName?.trim() || 'Unknown user';
}

/** The actor fields an audit entry carries over the wire. */
export interface AuditActorFields {
  actor_name?: string | null;
  /** The impersonating admin's email. Since EE-531 the server sends it to global admins only. */
  impersonated_by?: string | null;
  /** EE-531: "EasyEviction support (on behalf of Jane Doe)" for a support-session change, else the actor's name. */
  actor_display?: string | null;
  impersonated?: boolean | null;
}

/**
 * Names an audit entry's actor (EE-531).
 *
 * A firm user no longer receives the support admin's email: the server sends `impersonated: true` and a ready-made
 * `actor_display` instead, which is rendered as is. A global admin still receives `impersonated_by` and keeps the
 * email form, and so does an older server that sends neither new field.
 */
export function describeAuditEntryActor(entry: AuditActorFields | null | undefined): string {
  const display = entry?.actor_display?.trim();
  if (entry?.impersonated && display && !entry.impersonated_by?.trim()) {
    return display;
  }
  return describeAuditActor(entry?.actor_name, entry?.impersonated_by);
}

/**
 * Names whoever did something, preferring the server's support-aware `*_display` field (EE-531).
 *
 * The service sends `<field>_display` next to the raw name: "EasyEviction support (on behalf of Jane Doe)" for an act
 * done in a support session, otherwise the plain name. An older server sends no display field, so the raw name is
 * the fallback, and a blank display counts as absent.
 */
export function actorName(name: string | null | undefined, display?: string | null): string | null {
  return display?.trim() || name || null;
}

/** Appended to a name resolved from a user id when that user acted in a support session (Filing Desk). */
export const SUPPORT_SESSION_TAG = '(EasyEviction support)';

/** `name`, tagged as a support-session act when `impersonated` is set (EE-531). */
export function withSupportTag(name: string, impersonated?: boolean | null): string {
  return impersonated ? `${name} ${SUPPORT_SESSION_TAG}` : name;
}
